import { useGateValue } from "@statsig/react-bindings";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useLocation } from "wouter";
import { pendingThreadMessagesAtom } from "@/atoms/messagesAtoms.ts";
import { parseUnitsExhaustedError } from "@/components/billing/UnitsExhaustedDialog.tsx";
import { buildOptimisticUserMessage } from "@/components/messages/hooks/useSendMessage.tsx";
import { Button } from "@/components/ui/button.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { createNewTopicThread, updateThreadOptimistically } from "@/hooks/useThreads.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { api } from "@/lib/http.ts";
import { ASSISTANT_IMPORT_GATE, IMPORT_FROM_ASSISTANT_MESSAGE } from "@/shared/assistantImport.ts";
import type { NewMessageReq, NewMessageResp } from "@/types/threadEvents.ts";

/**
 * Starts an import from another assistant: a new thread with the request
 * already sent, so the assistant picks it up by asking which app the user is
 * coming from. Resolves to the thread and navigates to it.
 */
const useStartAssistantImport = () => {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { getUserTimezone } = useCurrentUserContext();
  const setPendingThreadMessages = useSetAtom(pendingThreadMessagesAtom);

  return useMutation({
    mutationFn: async () => {
      const { id: threadId } = await createNewTopicThread({ title: "", artifactIds: [] });
      const body: NewMessageReq = {
        text: IMPORT_FROM_ASSISTANT_MESSAGE,
        type: "userRequest",
        clientContext: { userTimezone: getUserTimezone() },
      };
      const { data } = await api.post<NewMessageResp>(`/api/a/threads/${threadId}/messages`, body);
      // Hand the sent request to the thread keyed on its real event id, as the
      // composer does for a new thread. Without it the message sits in the
      // queued pill until the actor picks it up.
      if (data.eventId !== undefined) {
        const { item } = buildOptimisticUserMessage(threadId, body, {
          status: "completed",
          suppressQueuedPill: true,
          id: data.eventId,
        });
        setPendingThreadMessages((prev) => ({ ...prev, [threadId]: item }));
      }
      return threadId;
    },
    onSuccess: (threadId) => {
      // Shows the stop control and the working state before the first socket
      // update arrives, as a thread started from the composer does.
      updateThreadOptimistically(queryClient, threadId, { activityState: "running" });
      setLocation(`/threads/${threadId}`);
    },
    onError: (error) => {
      // The upgrade dialog already explains a refusal for lack of AI usage.
      if (!parseUnitsExhaustedError(error)) errorToast("Couldn't start the import");
    },
  });
};

/** The Account settings entry point for the import, shown while its gate is on. */
export const ImportFromAssistantCard = () => {
  const enabled = useGateValue(ASSISTANT_IMPORT_GATE);
  const start = useStartAssistantImport();

  if (!enabled) return null;

  return (
    <div>
      <h2 className="text-base font-semibold mb-2">Import from another assistant</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Bring your memories, skills and routines over from ChatGPT, Claude, Gemini or another
        assistant. Okthx walks you through it in a new chat.
      </p>
      <Button
        variant="gradientOutline"
        className="duration-150"
        disabled={start.isPending}
        onClick={() => start.mutate()}
        data-statsig-name="settings-account-import-from-assistant"
      >
        Start import
      </Button>
    </div>
  );
};
